import { useState, type FormEvent } from 'react'
import { EyeIcon, EyeOffIcon, Loader2Icon, MailCheckIcon } from 'lucide-react'
import { useAuth } from '@/lib/firebase/auth'
import { authErrorMessage } from '@/lib/firebase/auth-errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Mode = 'signin' | 'signup' | 'reset' | 'link'

const SUBMIT_LABEL: Record<Mode, string> = {
  signin: 'Sign in',
  signup: 'Create account',
  reset: 'Send reset link',
  link: 'Email me a sign-in link',
}

export function EmailAuthForm() {
  const { signInWithEmail, signUpWithEmail, sendPasswordReset, sendSignInLink } = useAuth()

  const [mode, setMode] = useState<Mode>('link')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const switchTo = (next: Mode) => {
    setMode(next)
    setError(null)
    setSent(false)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      if (mode === 'signin') {
        await signInWithEmail(email, password)
      } else if (mode === 'signup') {
        await signUpWithEmail(email, password, name)
      } else if (mode === 'link') {
        await sendSignInLink(email)
        setSent(true)
      } else {
        await sendPasswordReset(email)
        setSent(true)
      }
      // On success the auth state flips and AppShell replaces this whole tree.
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="w-full space-y-3 text-left">
        <div className="flex items-start gap-3 rounded-xl border bg-card p-4 text-sm shadow-xs">
          <MailCheckIcon className="mt-0.5 size-4 shrink-0 text-primary" />
          {mode === 'link' ? (
            <p className="text-muted-foreground">
              We sent a sign-in link to{' '}
              <span className="font-medium text-foreground">{email.trim()}</span>. Open it on this
              device to finish signing in — check your spam folder if it doesn't arrive.
            </p>
          ) : (
            <p className="text-muted-foreground">
              If <span className="font-medium text-foreground">{email.trim()}</span> has an
              account, a password reset link is on its way.
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" className="w-full" onClick={() => switchTo(mode === 'link' ? 'link' : 'signin')}>
          Back to sign in
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="w-full space-y-3 text-left">
      {mode === 'signup' && (
        <div className="space-y-1.5">
          <Label htmlFor="auth-name">Name</Label>
          <Input
            id="auth-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            placeholder="Ada Lovelace"
            disabled={busy}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="auth-email">Email</Label>
        <Input
          id="auth-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          required
          disabled={busy}
        />
      </div>

      {(mode === 'signin' || mode === 'signup') && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="auth-password">Password</Label>
            {mode === 'signin' && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => switchTo('reset')}
              >
                Forgot?
              </button>
            )}
          </div>
          <div className="relative">
            <Input
              id="auth-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={mode === 'signup' ? 6 : undefined}
              className="pr-9"
              required
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
            </button>
          </div>
          {mode === 'signup' && (
            <p className="text-xs text-muted-foreground">At least 6 characters.</p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" className="w-full gap-2" disabled={busy}>
        {busy && <Loader2Icon className="size-4 animate-spin" />}
        {SUBMIT_LABEL[mode]}
      </Button>

      {(mode === 'signin' || mode === 'link') && (
        <button
          type="button"
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
          onClick={() => switchTo(mode === 'link' ? 'signin' : 'link')}
        >
          {mode === 'link' ? 'Use a password instead' : 'Sign in without a password'}
        </button>
      )}

      {/* A sign-in link also creates the account, so only password modes need this. */}
      {mode !== 'link' && (
        <p className="text-center text-xs text-muted-foreground">
          {mode === 'signup' ? 'Already have an account?' : 'New to VOYP?'}{' '}
          <button
            type="button"
            className="font-medium text-foreground hover:underline"
            onClick={() => switchTo(mode === 'signup' ? 'signin' : 'signup')}
          >
            {mode === 'signup' ? 'Sign in' : 'Create an account'}
          </button>
        </p>
      )}
    </form>
  )
}
